import type { RequestMetadataRow } from '../electron/search-db';
import { resolvePortalTargetScope } from '../app/providers/portal-tools/portal-tool-handler.utils';

type HistoryRequestHitlDisplay = {
  method: string;
  url: string;
  statusCode?: number;
  rawResult?: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  wasProxyModified?: boolean;
};

function parseHeadersFromJson(
  raw: string | null,
): Record<string, string | string[]> {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const headers: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') {
        headers[key] = value;
      } else if (Array.isArray(value)) {
        headers[key] = value.map(String);
      }
    }
    return headers;
  } catch (error: unknown) {
    console.warn(
      '[loadHistoryRequestForHitlDisplay] Failed to parse headers from JSON',
      {
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
    return {};
  }
}

function buildHitlDisplayArgsFromHistoryRow({
  row,
  requestBody,
  responseBody,
}: {
  row: RequestMetadataRow;
  requestBody: string;
  responseBody: string;
}): HistoryRequestHitlDisplay {
  return {
    method: row.method,
    url: row.url,
    statusCode: row.status_code ?? undefined,
    rawResult: responseBody || undefined,
    headers: parseHeadersFromJson(row.request_headers),
    wasProxyModified: row.was_proxy_modified === 1,
    ...(requestBody ? { body: requestBody } : {}),
  };
}

export async function loadHistoryRequestForHitlDisplay({
  requestId,
}: {
  requestId: string;
}): Promise<HistoryRequestHitlDisplay | null> {
  if (!window.electronAPI?.history?.getRows) {
    return null;
  }

  const { targetId, targetType } = resolvePortalTargetScope({});
  const historyResult = await window.electronAPI.history.getRows({
    targetId,
    targetType,
    startRow: 0,
    endRow: 1,
    sortModel: [{ colId: 'timestamp', sort: 'desc' }],
    filters: {
      tqlWhereClause: 'r.id = ?',
      tqlParams: [requestId],
    },
  });

  if (!historyResult.success || !historyResult.data?.rows[0]) {
    return null;
  }

  const row = historyResult.data.rows[0];
  const bodyResult = await window.electronAPI.bodyDb.get(requestId);
  if (!bodyResult.success) {
    console.warn(
      '[loadHistoryRequestForHitlDisplay] Failed to load request/response bodies',
      {
        requestId,
        error: bodyResult.error,
      },
    );
    return null;
  }

  const requestBody = bodyResult.data?.requestBody ?? '';
  const responseBody = bodyResult.data?.responseBody ?? '';

  return buildHitlDisplayArgsFromHistoryRow({
    row,
    requestBody,
    responseBody,
  });
}
